import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import groupBy from 'lodash/groupBy';

import {
  CerType,
  ContentPointQuestionType,
  Subject,
  Years,
  OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS,
} from 'global/constants';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import { OutcomeOption } from 'lib/form/tag-autocomplete/outcomes/tag-autocomplete-outcomes.constants';
import { SEARCH_OUTCOMES } from 'approot/shared/graphql/outcomes/outcomes.graphql';
import type {
  SearchOutcomesQuery,
  SearchOutcomesQueryVariables,
} from '../../../../../shared/graphql/outcomes/outcomes.graphql.generated';
import {
  mapOutcomesToOptions,
  mapOutcomeToOption,
} from 'approot/shared/outcomes/outcome.utils';
import { FormData } from 'approot/shared/outcomes/outcome.constants';
import { Outcome } from 'approot/shared/outcomes/outcome.constants';

const PERFORMANCE_EXPECTATION_FIELD = OUTCOME_CATEGORY_PERFORMANCE_EXPECTATIONS;

export const emptyKeyActivityFormState = (): FormData => ({
  [PERFORMANCE_EXPECTATION_FIELD]: [],
});

type UsePerformanceExpectationStandardsProps = {
  subject: Subject;
  years: Years[];
  /** Optional initial state e.g. from existing activity outcomes (with category). */
  savedOutcomes?: Outcome[] | null;
  /** Optional explicit initial form state (e.g. for a new PE block). Overrides savedOutcomes-derived state. */
  initialFormState?: FormData;
  initialQuestionTypes?: ContentPointQuestionType[];
  initialCerTypes?: CerType[];
};

/**
 * Hook for Performance Expectation Standards form: local outcome state and search by PE,
 * without persisting on add/remove. Use for activity builder Save/Cancel flow.
 *
 * Owns the whole PE block draft — outcomes plus the block-level Question Types and
 * CER — so clearAll resets every part of it. Splitting the draft across the hook and
 * the form let a dismissed PE's pills survive onto the next PE picked.
 */
export const usePerformanceExpectationStandards = ({
  subject,
  years,
  savedOutcomes,
  initialFormState,
  initialQuestionTypes,
  initialCerTypes,
}: UsePerformanceExpectationStandardsProps) => {
  const [searchOutcomes, { data: outcomesData, loading: outcomesLoading }] =
    useLoggingLazyQuery<SearchOutcomesQuery, SearchOutcomesQueryVariables>(
      SEARCH_OUTCOMES
    );

  const initialPeLoadDone = useRef<boolean>(false);

  const savedOutcomeGroups = useMemo(
    () => (savedOutcomes ? groupBy(savedOutcomes, 'category') : {}),
    [savedOutcomes]
  );

  const defaultValues: FormData = useMemo(() => {
    const values: FormData = {};
    for (const k in savedOutcomeGroups) {
      values[k] = savedOutcomeGroups[k].map((o: Outcome) =>
        mapOutcomeToOption(o)
      );
    }
    return values;
  }, [savedOutcomeGroups]);

  const [formState, setFormState] = useState<FormData>(
    initialFormState ?? defaultValues
  );

  const [questionTypes, setQuestionTypes] = useState<
    ContentPointQuestionType[]
  >(initialQuestionTypes ?? []);
  const [cerTypes, setCerTypes] = useState<CerType[]>(initialCerTypes ?? []);

  useEffect(() => {
    if (initialPeLoadDone.current) return;
    if (defaultValues[PERFORMANCE_EXPECTATION_FIELD]?.length) return;
    initialPeLoadDone.current = true;
    searchOutcomes({
      variables: {
        input: {
          subjects: [subject],
          years,
          category: PERFORMANCE_EXPECTATION_FIELD,
        },
      },
    });
  }, [defaultValues, subject, years, searchOutcomes]);

  const outcomeOptions = useMemo(
    () =>
      mapOutcomesToOptions(
        'category',
        outcomesData?.searchOutcomes ?? [],
        savedOutcomes ?? []
      ),
    [outcomesData?.searchOutcomes, savedOutcomes]
  );

  const setPerformanceExpectation = useCallback(
    (option: OutcomeOption) => {
      setFormState(prev => ({
        ...prev,
        [PERFORMANCE_EXPECTATION_FIELD]: [option],
      }));
      searchOutcomes({
        variables: {
          input: {
            performanceExpectationId: option.value,
            subjects: [subject],
            years,
          },
        },
      });
    },
    [searchOutcomes, subject, years]
  );

  const clearAll = useCallback(() => {
    setFormState({ [PERFORMANCE_EXPECTATION_FIELD]: [] });
    setQuestionTypes([]);
    setCerTypes([]);
    searchOutcomes({
      variables: {
        input: {
          subjects: [subject],
          years,
          category: PERFORMANCE_EXPECTATION_FIELD,
        },
      },
    });
  }, [searchOutcomes, subject, years]);

  const initialPeOutcomeId = useMemo(() => {
    const pe = savedOutcomeGroups[PERFORMANCE_EXPECTATION_FIELD];
    return pe?.[0]?.id;
  }, [savedOutcomeGroups]);

  const initialPeIdFromFormState = useMemo(
    () => initialFormState?.[PERFORMANCE_EXPECTATION_FIELD]?.[0]?.value,
    [initialFormState]
  );

  const peIdForDimensionSearch = initialPeOutcomeId ?? initialPeIdFromFormState;

  useEffect(() => {
    if (peIdForDimensionSearch == null) return;
    searchOutcomes({
      variables: {
        input: {
          performanceExpectationId: peIdForDimensionSearch,
          subjects: [subject],
          years,
        },
      },
    });
  }, [peIdForDimensionSearch, searchOutcomes, subject, years]);

  return {
    formState,
    setFormState,
    questionTypes,
    setQuestionTypes,
    cerTypes,
    setCerTypes,
    searchOutcomes,
    outcomeOptions,
    outcomesLoading,
    setPerformanceExpectation,
    clearAll,
    performanceExpectationField: PERFORMANCE_EXPECTATION_FIELD,
  };
};
